<template>
  <div class="attendance-report">
    <!-- Summary Counts -->
    <div class="summary-container">
      <div class="summary-card">
        <h4>Total Classes</h4>
        <p v-if="loadingReport || loading">  <b-spinner small></b-spinner></p>
        <p v-else>{{ summary.totalClasses }}</p>
      </div>
      <div class="summary-card">
        <h4>Attended Classes</h4>
        <p v-if="loadingReport || loading">  <b-spinner small></b-spinner></p>
        <p v-else>{{ summary.attendedClasses }}</p>
      </div>
      <div class="summary-card">
        <h4>Missed Classes</h4>
        <p v-if="loadingReport || loading">  <b-spinner small></b-spinner></p>
        <p v-else>{{ summary.missedClasses }}</p>
      </div>
      <div class="summary-card">
        <h4>Upcoming Classes</h4>
        <p v-if="loadingReport || loading">  <b-spinner small></b-spinner></p>
        <p v-else>{{ summary.upcomingClasses }}</p>
      </div>
    </div>

    <!-- Table -->
    <div class="table-container">
      <table v-if="!loadingReport && !loading && contents.length > 0" class="attendance-table">
        <thead>
          <tr>
            <th>Title</th>
            <th>Status</th>
            <th>Attended From</th>
            <th>Attended Duration</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(content, index) in contents" :key="content.id || index">
            <td>
              <div class="d-flex flex-column gap-2 title">
                <b>{{ content.title }}</b>
                <p>{{ convertToDatetimeLocal(content.availableFrom) }}</p>
                <p>{{ formatDuration(content.availableFrom, content.availableTo) }}</p>
              </div>
            </td>
            <td>
              <span :class="[
                'status',
                {
                  'status-present': getAttendanceStatus(content.id).toLowerCase() === 'present',
                  'status-absent': getAttendanceStatus(content.id).toLowerCase() === 'absent'
                }
              ]">
                {{ getAttendanceStatus(content.id) }}
              </span>
            </td>
            <td>
              <span class="attended_form">
                {{ getAttendedFromValue(content.id) }}
              </span>
            </td>
            <td>
              <span>
                {{ getAttendedDuration(content.id) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="loading" v-else-if="loadingReport || loading">
        <b-spinner small></b-spinner>
      </div>
      <div v-else-if="!loadingReport && !loading && contents.length == 0" class="empty-attendance-container">
        <div class="empty-attendance-icon">
          <i class="fas fa-calendar-times"></i>
        </div>
        <h5 class="empty-attendance-title">No Attendance Records</h5>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { ContentType } from '../../../constants/content';

export default {
  name: 'LiveClassAttendance',
  props: {
    studentId: { type: String, required: true },
    productVariantId: { type: String, required: true },
  },
  data() {
    return {
      loading: false,
      loadingReport : false,
      contents: [],
      contentsMap: new Map(),
      attendanceReport: new Map(),
      currentDateTime: undefined,
      summary: {
        totalClasses: 0,
        attendedClasses: 0,
        missedClasses: 0,
        upcomingClasses: 0
      }
    };
  },
  methods: {
    ...mapActions("appState", ["fetchCurrentDateTimeInUTC"]),
    ...mapActions("content", ["fetchContentListByProductVariant", "fetchLiveClassAttendanceSummary"]),

    async fetchLiveClassData(userIds) {
      try {
        this.loadingReport = true;
        
        // Ensure contents is loaded before proceeding
        if (!this.contents || this.contents.length === 0) {
          this.loadingReport = false;
          return;
        }

        const contentIds = this.contents.map(c => Number(c.id));

        const userIdsAsIntegers = userIds.map(id => Number(id));


        const dataList = await this.fetchLiveClassAttendanceSummary({ 
          contentIds, 
          userIds: userIdsAsIntegers 
        });

        if (dataList && dataList.length > 0) {

          this.attendanceReport = new Map(dataList.map((record) => {
            const contentId = String(record.contentID);

            const attendedSeconds = record.attendedDurationInSeconds ?
                Number(record.attendedDurationInSeconds) : 0;
            const durationInMinutes = Math.round(attendedSeconds / 60);

            const recordData = {
              contentID: record.contentID,
              status: record.liveClassAttendanceStatus,
              attendedFrom: record.attendedFrom,
              duration: durationInMinutes
            };

            return [ contentId, recordData ]
          }));
        }

      } catch (error) {
        console.error('Error fetching live class data:', error);
        this.$toasted?.error("Failed to fetch live class data", {
          icon: "exclamation-triangle",
          duration: 3000,
          theme: "outline",
          position: "bottom-center",
        });
      } finally {
        this.loadingReport = false;
      }
    },

    async fetchContentList() {
      try {
        this.loading = true;
        const res = await this.fetchContentListByProductVariant({ 
          id: this.productVariantId,
          contentType: ContentType.LIVE_CLASS 
        });

        this.contents = res || [];

        this.contentsMap = new Map(this.contents.map((c) => {
          return [ c.id, c ]
        }));
        
      } catch (error) {
        console.error('Error fetching content list:', error);
      } finally {
        this.loading = false;
      }
    },

    convertToDatetimeLocal(dateTimeString) {
      const date = new Date(dateTimeString);
      return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  },

    formatDuration(from, to) {
      const ms = new Date(to) - new Date(from);
      if (ms <= 0) return "0 mins";
      const minutes = Math.floor(ms / 60000);
      if (minutes < 60) return `${minutes} mins`;
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours}h ${mins}m`;
  },

    formatMinutesToHM(minutes) {
      if (!minutes || minutes <= 0) return "0m";
      if (minutes < 60) return `${minutes}m`;
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours}h ${mins}m`;
    },

    getAttendedFromValue(contentId) {
      if (!this.attendanceReport.has(contentId)) return '-';

      const record = this.attendanceReport.get(contentId);

      const attendedFrom = record.status.toLowerCase() === 'present'
          ? record.attendedFrom
          : '-';

      return attendedFrom.toLowerCase() === 'physical' ? 'In Person' : 'Online';
    },

    getAttendanceStatus(contentId) {
      const content = this.contentsMap.get(contentId);

      if (!content) return null;

      if(new Date(content.availableTo) <= this.currentDateTime) {
        const attendanceInfo = this.attendanceReport.get(contentId);

        if(attendanceInfo) {
          return attendanceInfo.status;
        }

        return 'Absent';
      } else {
        return 'Upcoming'
      }
    },

    getAttendedDuration(contentId) {
      const content = this.contentsMap.get(contentId);

      if (!content) return null;

      if(new Date(content.availableTo) <= this.currentDateTime) {
        const attendanceInfo = this.attendanceReport.get(contentId);

        if(!attendanceInfo) {
          return '-';
        }

        return this.formatMinutesToHM(attendanceInfo.duration);
      }

      return '-';
    },

    calculateSummary() {
      let totalClasses = 0;
      let attendedClasses = 0;
      let missedClasses = 0;
      let upcomingClasses = 0;

      this.contents.forEach(content => {

        if(new Date(content.availableTo) > this.currentDateTime) {
          upcomingClasses++;
        } else {
          if (this.attendanceReport.has(content.id)) {
            const status = this.attendanceReport.get(content.id).status.toLowerCase();

            if (status === 'present') {
              attendedClasses++;
            } else if (status === 'absent') {
              missedClasses++;
            }
          } else {
            missedClasses++;
          }
        }

        totalClasses++;

      });

      this.summary.totalClasses = totalClasses;
      this.summary.attendedClasses = attendedClasses;
      this.summary.missedClasses = missedClasses;
      this.summary.upcomingClasses = upcomingClasses;
    }
  },

  async created() {
    const currentDateTimeInUTCInfo = await this.fetchCurrentDateTimeInUTC();

    console.log({currentDateTimeInUTCInfo});

    if(currentDateTimeInUTCInfo) {
      this.currentDateTime = new Date(currentDateTimeInUTCInfo.currentUnixTimestamp);
    }

    await this.fetchContentList();
    
    if (this.contents && this.contents.length > 0) {
      await this.fetchLiveClassData([this.studentId]);

      this.calculateSummary();

    } else {
      this.loading = false;
      this.loadingReport = false;
    }
  },
};
</script>
  
  <style scoped>
  .attendance-report {
    padding: 1rem 0;
    padding-top: 0;
 }

.summary-container {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  justify-content: center;
}

.summary-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  padding: 1rem;
  text-align: center;
  min-width: 120px;
  flex: 1;
}

.summary-card h4 {
  margin: 0;
  font-size: 1rem;
  color: #2c3e50;
}

.summary-card p {
  margin: 0.5rem 0 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #34495e;
}
 
  .table-container {
    overflow-x: auto;
    border-radius: 0 !important;
    max-height: 80vh;
  }
  
  .attendance-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    background: #fff;
    border-radius: 10px;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  }
  .attendance-table tr:hover{
    background: #f2f2f2 !important;
  }

  .title p{
    margin: 0;
  }
  
  .attendance-table th,
  .attendance-table td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid #eee;
  }
  
  .attendance-table th {
    background: #f8f9fa;
    font-weight: 600;
    color: #2c3e50;
    position: sticky;
    top: 0;
    z-index: 1;
  }
  
  .attendance-table tr.even-row {
    background: #f9f9f9;
  }
  
  .attendance-table tr:hover {
    background: #e0f7fa;
    transition: background 0.3s ease;
  }
  
  .attendance-table td {
    color: #34495e;
    font-size: 1rem;
  }
  
  .status-present {
    color: #2ecc71;
    font-weight: 500;
  }
  
  .status-absent {
    color: #e74c3c;
    font-weight: 500;
  }
  
  .text-muted {
    color: #7f8c8d;
  }
  
  .content-type-icon {
    height: 2.5rem;
  }

  .status,
  .attended_form {
    text-transform: capitalize;
  }
  .loading{
    display: flex;
    justify-content: center;
    margin: 2rem;
    color: var(--primary-color);
  }

.empty-container,
.empty-attendance-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  padding: 2rem;
  text-align: center;
  color: #6c757d;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  background: #f8f9fa;
}

.empty-icon,
.empty-attendance-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-title,
.empty-attendance-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #495057;
}

.empty-message,
.empty-attendance-message {
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0;
}

  @media (max-width: 767px) {
    .table-container {
      max-width: 600px;
    }
    .attendance-table th,
    .attendance-table td {
      padding: 0.75rem;
      font-size: 0.9rem;
    }
    .summary-card {
      width: 100%;
      max-width: 300px;
    }
  }
  </style>